import { Divider, Typography } from 'antd'
import {
  DownwardIndicator,
  FfsResultIcon,
} from 'features/indicationTriage/cards/ffsCalc/FfsResultIcon'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { useRsfValues } from 'features/indicationTriage/cards/ffsCalc/mawp/calculateRsf'
import {
  FfsMawpSection,
  useFfsMawpProps,
} from 'features/indicationTriage/cards/ffsCalc/mawp/FfsMawpSection'
import { OptionalSection } from 'features/indicationTriage/cards/ffsCalc/optionalSection/OptionalSection'
import { useDebouncedLoading } from 'features/indicationTriage/cards/ffsCalc/useDebouncedLoading'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import React, { FC, useContext } from 'react'
import { GeckoComponentDataFrame, GeckoComponentModel } from 'types'

import { useFinalAcceptabilityCurveData } from './FfsAcceptabilityCriteriaCurve'
import {
  FfsDataValueSection,
  useFfsLevelOneDataValues,
} from './FfsDataValuesSection'
import {
  FfsFinalAcceptabilityCriteraSection,
  useLevelOneFinalAcceptabilityCriteriaValues,
} from './FfsFinalAcceptabilityCriteraSection'
import {
  FfsFlawSizeCriteriaSection,
  useLevelOneFlawSizeCriteriaCheck,
} from './FfsFlawSizeCriteriaSection'
import { FfsUserInputSection } from './FfsLevelOneUserInputSection'

export interface FfsLevelOneCalcProps {
  componentModel?: GeckoComponentModel
  thicknessCdf?: GeckoComponentDataFrame
}

const { Title } = Typography

const SPIN_DEBOUNCE_DELAY = 300

const RawFfsLevelOneCalc: FC<FfsLevelOneCalcProps> = ({
  componentModel,
  thicknessCdf,
}) => {
  const { fca, fcaMl } = useContext(FfsSharedValuesContext)
  const { indication: indicationItem } = useIndicationData()
  const ffsDataValues = useFfsLevelOneDataValues({
    indicationItem,
    fca,
    componentModel,
    thicknessCdf,
  })
  const {
    loading: dataValuesLoading,
    tc,
    tmm,
    innerDiameter,
    outerDiameter,
    lambda,
  } = ffsDataValues

  const flawSizeCheckResult = useLevelOneFlawSizeCriteriaCheck({
    componentType: componentModel?.type,
    fca,
    tmm,
    fcaMl,
    tc,
    d: innerDiameter,
    indicationId: indicationItem?.id,
  })
  const {
    criteriaValues: flawSizeCriteriaCheckValues,
    isLoading: flawSizeCriteriaLoading,
  } = flawSizeCheckResult

  const loading = useDebouncedLoading(SPIN_DEBOUNCE_DELAY, [
    flawSizeCriteriaLoading,
    dataValuesLoading,
  ])

  const finalAcceptabilityCriteraValues =
    useLevelOneFinalAcceptabilityCriteriaValues({
      lambda,
      flawSizeCriteriaCheckValues,
    })
  const { foliasFactor, rsfa } = finalAcceptabilityCriteraValues

  const { rsf, rt } = useRsfValues({ tmm, fcaMl, tc, fca, mt: foliasFactor })
  const finalAcceptabilityCurveData = useFinalAcceptabilityCurveData({
    lambda: lambda?.num ?? null,
    mt: foliasFactor?.num,
    rsf: rsf?.num ?? null,
    rsfa: rsfa?.num,
    rt: rt?.num ?? null,
  })

  const mawpProps = useFfsMawpProps({
    tc,
    tmm,
    outerDiameter,
    rsfa,
    rsf,
  })

  return (
    <>
      <Title level={4}>User Parameters</Title>
      <FfsUserInputSection />
      <Divider />
      <Title level={4}>Data Values</Title>
      <FfsDataValueSection {...ffsDataValues} />
      <Divider />
      <Title level={4}>Flaw Size Criteria</Title>
      <FfsFlawSizeCriteriaSection
        flawSizeCheckResult={flawSizeCheckResult}
        loading={loading}
        componentModel={componentModel}
      />
      <Divider />
      <Title level={4}>Final Acceptability Criteria</Title>
      <FfsFinalAcceptabilityCriteraSection
        {...finalAcceptabilityCriteraValues}
        curveDataProps={finalAcceptabilityCurveData}
        lambda={lambda}
        loading={loading}
      />
      <Divider>
        <FfsResultIcon
          passed={finalAcceptabilityCurveData.passesFac}
          tooltipFailureText='Failed acceptability criteria. Derating required (below)'
          trailingFail={DownwardIndicator}
        />
      </Divider>
      <OptionalSection visible={!finalAcceptabilityCurveData.passesFac}>
        <Title level={4}>MAWP</Title>
        <FfsMawpSection {...mawpProps} loading={loading} />
      </OptionalSection>
    </>
  )
}

export const FfsLevelOneCalc = React.memo(RawFfsLevelOneCalc)
